import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Undo2 } from 'lucide-react';

import { mapRuleGroupIdToApiPathParam } from 'api/warehouse/batches';
import {
    useExecuteReverseSubstitutions,
    usePreviewReverseSubstitutions,
} from 'components/shipping/components/viewBatches/queries/BatchQueries';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

const STEP_INPUT = 'input';
const STEP_PREVIEW = 'preview';
const STEP_RESULTS = 'results';

/**
 * @param {unknown} value
 * @returns {string}
 */
function formatSkuLabel(value) {
    if (value === undefined || value === null || value === '') {
        return '—';
    }
    return String(value);
}

/**
 * Groups substitution rules by original SKU so multi-item replacements render as one original
 * with several substitutes.
 *
 * @param {Array<Record<string, unknown>>} rules
 * @returns {Array<{ originalExternalSku: unknown, originalName: unknown, substitutes: Array<Record<string, unknown>>, hasEnabledRule: boolean }>}
 */
export function groupRulesByOriginal(rules) {
    const groups = [];
    const indexBySku = new Map();

    for (const rule of rules) {
        const skuKey = rule?.originalExternalSku;
        const existingIndex = indexBySku.get(skuKey);

        if (existingIndex === undefined) {
            indexBySku.set(skuKey, groups.length);
            groups.push({
                originalExternalSku: rule?.originalExternalSku,
                originalName: rule?.originalName,
                substitutes: [rule],
                hasEnabledRule: rule?.enabled === true,
            });
        } else {
            const group = groups[existingIndex];
            group.substitutes.push(rule);
            if (rule?.enabled === true) {
                group.hasEnabledRule = true;
            }
        }
    }

    return groups;
}

/**
 * Multi-step dialog: enter rule group → preview → confirm → results.
 *
 * @param {object} props
 * @param {number | string | null} [props.batchId] - Batch being reversed
 * @param {number | string | null} [props.facilityId] - Current facility (must match batch)
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {(batchId: number | string) => void} [props.onRequestApplySubstitutions] - After successful reversals
 * @returns {JSX.Element}
 */
export function ReverseSubstitutionDialog({ batchId, facilityId, onOpenChange, onRequestApplySubstitutions, open }) {
    const [step, setStep] = useState(STEP_INPUT);
    const [ruleGroupIdInput, setRuleGroupIdInput] = useState('');
    const [previewedRuleGroupId, setPreviewedRuleGroupId] = useState(null);
    const [validationError, setValidationError] = useState('');
    const [preview, setPreview] = useState(null);
    const [results, setResults] = useState([]);
    const [requestError, setRequestError] = useState('');

    const isDialogActiveRef = useRef(open);
    const abortControllerRef = useRef(null);

    const resetState = useCallback(() => {
        setStep(STEP_INPUT);
        setRuleGroupIdInput('');
        setPreviewedRuleGroupId(null);
        setValidationError('');
        setPreview(null);
        setResults([]);
        setRequestError('');
    }, []);

    const previewMutation = usePreviewReverseSubstitutions({
        onSuccess: (data, variables) => {
            if (!isDialogActiveRef.current) return;
            setPreview(data ?? { rules: [], orderIds: [] });
            setPreviewedRuleGroupId(mapRuleGroupIdToApiPathParam(variables?.ruleGroupId) ?? null);
            setRequestError('');
            setStep(STEP_PREVIEW);
        },
        onError: (err) => {
            if (!isDialogActiveRef.current) return;
            if (err?.name === 'AbortError') return;
            setRequestError(err?.message ?? 'Failed to preview reverse substitutions');
            setPreview(null);
            setPreviewedRuleGroupId(null);
            setStep(STEP_INPUT);
        },
    });

    const executeMutation = useExecuteReverseSubstitutions({
        onSuccess: (data) => {
            if (!isDialogActiveRef.current) return;
            const nextResults = Array.isArray(data?.results) ? data.results : [];
            setResults(nextResults);
            setRequestError('');
            setStep(STEP_RESULTS);
        },
        onError: (err) => {
            if (!isDialogActiveRef.current) return;
            if (err?.name === 'AbortError') return;
            setRequestError(err?.message ?? 'Failed to reverse substitutions');
        },
    });

    const previewMutationRef = useRef(previewMutation);
    const executeMutationRef = useRef(executeMutation);
    previewMutationRef.current = previewMutation;
    executeMutationRef.current = executeMutation;

    const handleOpenChange = useCallback(
        (nextOpen) => {
            if (!nextOpen) {
                isDialogActiveRef.current = false;
                abortControllerRef.current?.abort();
                abortControllerRef.current = null;
                previewMutationRef.current.reset();
                executeMutationRef.current.reset();
                resetState();
            } else {
                isDialogActiveRef.current = true;
            }
            onOpenChange?.(nextOpen);
        },
        [onOpenChange, resetState],
    );

    useEffect(() => {
        isDialogActiveRef.current = open;
        if (!open) {
            abortControllerRef.current?.abort();
            abortControllerRef.current = null;
            previewMutationRef.current.reset();
            executeMutationRef.current.reset();
            resetState();
        }
    }, [open, resetState]);

    const normalizedRuleGroupId = mapRuleGroupIdToApiPathParam(ruleGroupIdInput.trim());
    const activeRuleGroupId = previewedRuleGroupId ?? normalizedRuleGroupId;
    const orderIds = Array.isArray(preview?.orderIds) ? preview.orderIds : [];
    const rules = Array.isArray(preview?.rules) ? preview.rules : [];
    const ruleGroups = groupRulesByOriginal(rules);
    const hasEnabledRule = rules.some((rule) => rule?.enabled === true);
    const hasSuccessfulReversal = results.some((result) => result?.outcome === 'succeeded');
    const isBusy = previewMutation.isPending || executeMutation.isPending;
    const canConfirmReverse = !isBusy && orderIds.length > 0 && !hasEnabledRule && activeRuleGroupId != null;

    const handlePreview = useCallback(() => {
        if (normalizedRuleGroupId === undefined) {
            setValidationError('Enter a valid rule group ID');
            return;
        }
        setValidationError('');
        setRequestError('');
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        previewMutation.mutate({
            batchId,
            facilityId,
            ruleGroupId: normalizedRuleGroupId,
            signal: abortController.signal,
        });
    }, [batchId, facilityId, normalizedRuleGroupId, previewMutation]);

    const handleConfirm = useCallback(() => {
        if (activeRuleGroupId == null || orderIds.length === 0 || hasEnabledRule) {
            return;
        }
        setRequestError('');
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        executeMutation.mutate({
            batchId,
            facilityId,
            ruleGroupId: activeRuleGroupId,
            signal: abortController.signal,
        });
    }, [activeRuleGroupId, batchId, executeMutation, facilityId, hasEnabledRule, orderIds.length]);

    const handleApplySubstitutions = useCallback(() => {
        if (batchId === undefined || batchId === null || batchId === '') {
            return;
        }
        onRequestApplySubstitutions?.(batchId);
        handleOpenChange(false);
    }, [batchId, handleOpenChange, onRequestApplySubstitutions]);

    const handleBackToInput = useCallback(() => {
        setStep(STEP_INPUT);
        setPreview(null);
        setPreviewedRuleGroupId(null);
        setRequestError('');
    }, []);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="flex max-h-[90vh] flex-col gap-4 overflow-hidden sm:max-w-4xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Undo2 className="h-5 w-5" aria-hidden="true" />
                        Reverse Substitution For Batch
                    </DialogTitle>
                    <DialogDescription>
                        {batchId != null && batchId !== ''
                            ? `Preview and reverse an FBM substitution rule group for batch ${batchId}.`
                            : 'Preview and reverse an FBM substitution rule group for this batch.'}
                    </DialogDescription>
                </DialogHeader>

                {requestError ? (
                    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                        {requestError}
                    </div>
                ) : null}

                {step === STEP_INPUT ? (
                    <div className="space-y-3">
                        <div className="space-y-2">
                            <Label htmlFor="reverse-sub-rule-group-id" className="mb-1.5">
                                Rule group ID
                            </Label>
                            <Input
                                id="reverse-sub-rule-group-id"
                                inputMode="numeric"
                                value={ruleGroupIdInput}
                                onChange={(event) => {
                                    setRuleGroupIdInput(event.target.value);
                                    if (validationError) {
                                        setValidationError('');
                                    }
                                }}
                                disabled={isBusy}
                                aria-invalid={Boolean(validationError) || undefined}
                            />
                            {validationError ? <p className="text-sm text-destructive">{validationError}</p> : null}
                        </div>
                        <DialogFooter className="gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => handleOpenChange(false)}
                                disabled={isBusy}
                            >
                                Cancel
                            </Button>
                            <Button type="button" onClick={handlePreview} disabled={isBusy}>
                                {previewMutation.isPending ? 'Loading preview…' : 'Preview'}
                            </Button>
                        </DialogFooter>
                    </div>
                ) : null}

                {step === STEP_PREVIEW ? (
                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
                        {hasEnabledRule ? (
                            <Alert className="border-amber-500 bg-amber-50 text-amber-950 [&>svg]:text-amber-700">
                                <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                                <AlertTitle className="text-base font-semibold">
                                    Substitution rule is still enabled
                                </AlertTitle>
                                <AlertDescription className="text-amber-900">
                                    Disable this rule group before reversing substitutions for the batch. Reversal is
                                    blocked while the rule group remains enabled.
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
                            <h3 className="text-sm font-medium">Rule Group {activeRuleGroupId}</h3>
                            {rules.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No rules returned for this group.</p>
                            ) : (
                                <div className="space-y-3">
                                    {ruleGroups.map((group, groupIndex) => {
                                        const substituteCount = group.substitutes.length;
                                        return (
                                            <div
                                                key={`${group.originalExternalSku}-${group.originalName}`}
                                                className="rounded-md border p-3"
                                            >
                                                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                                                    <div className="space-y-1">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                                                Original item
                                                            </p>
                                                            {groupIndex === 0 && hasEnabledRule ? (
                                                                <Badge variant="destructive">Enabled</Badge>
                                                            ) : null}
                                                            {groupIndex === 0 && !hasEnabledRule ? (
                                                                <Badge variant="neutral">Disabled</Badge>
                                                            ) : null}
                                                        </div>
                                                        <p className="font-medium">
                                                            {formatSkuLabel(group.originalName)}
                                                        </p>
                                                        <p className="text-sm text-muted-foreground">
                                                            SKU {formatSkuLabel(group.originalExternalSku)}
                                                        </p>
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">
                                                        Replaced by {substituteCount} item
                                                        {substituteCount === 1 ? '' : 's'}
                                                    </p>
                                                </div>
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>Substitute</TableHead>
                                                            <TableHead>Qty</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {group.substitutes.map((rule) => (
                                                            <TableRow
                                                                key={`${rule?.originalExternalSku}-${rule?.substituteExternalSku}-${rule?.substituteQuantity}-${rule?.enabled}-${rule?.substituteName}`}
                                                            >
                                                                <TableCell className="align-top">
                                                                    <div className="font-medium">
                                                                        {formatSkuLabel(rule?.substituteName)}
                                                                    </div>
                                                                    <div className="text-muted-foreground">
                                                                        SKU{' '}
                                                                        {formatSkuLabel(rule?.substituteExternalSku)}
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="align-top">
                                                                    {formatSkuLabel(rule?.substituteQuantity)}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="space-y-1 pt-2 text-sm">
                                <p>
                                    {orderIds.length} eligible order{orderIds.length === 1 ? '' : 's'}
                                </p>
                                {orderIds.length > 0 ? (
                                    <p className="max-h-24 overflow-y-auto text-muted-foreground">
                                        {orderIds.join(', ')}
                                    </p>
                                ) : (
                                    <p className="text-muted-foreground">
                                        Nothing to reverse right now. You can still review the rules above.
                                    </p>
                                )}
                            </div>
                        </div>

                        <DialogFooter className="gap-2">
                            <Button type="button" variant="outline" onClick={handleBackToInput} disabled={isBusy}>
                                Back
                            </Button>
                            <Button type="button" onClick={handleConfirm} disabled={!canConfirmReverse}>
                                {executeMutation.isPending ? 'Reversing…' : 'Confirm Reverse'}
                            </Button>
                        </DialogFooter>
                    </div>
                ) : null}

                {step === STEP_RESULTS ? (
                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
                        <div className="min-h-0 flex-1 overflow-y-auto">
                            {results.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No results returned.</p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Order ID</TableHead>
                                            <TableHead>Outcome</TableHead>
                                            <TableHead>Shipments</TableHead>
                                            <TableHead>Detail</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {results.map((result) => (
                                            <TableRow key={`${result?.orderId}-${result?.outcome}-${result?.detail}`}>
                                                <TableCell className="align-top whitespace-nowrap">
                                                    {formatSkuLabel(result?.orderId)}
                                                </TableCell>
                                                <TableCell className="align-top whitespace-nowrap">
                                                    {formatSkuLabel(result?.outcome)}
                                                </TableCell>
                                                <TableCell className="align-top whitespace-nowrap">
                                                    {formatSkuLabel(result?.shipmentCount)}
                                                </TableCell>
                                                <TableCell className="align-top whitespace-normal break-words">
                                                    {formatSkuLabel(result?.detail)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </div>
                        <DialogFooter className="gap-2">
                            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                                Close
                            </Button>
                            {hasSuccessfulReversal ? (
                                <Button type="button" onClick={handleApplySubstitutions}>
                                    Apply Additional Substitutions
                                </Button>
                            ) : null}
                        </DialogFooter>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

ReverseSubstitutionDialog.displayName = 'ReverseSubstitutionDialog';
